import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { stageRig } from '../lighting/stage-light-rig.js';
import { DustLayer } from '../particle-layers/dust-layer.js';
import { StormLayer } from '../particle-layers/storm-layer.js';

// Shared-rig contract (see the framing addendum): this visualizer's
// declared relationship to the one shared StageLightRig instance, and the
// beam intensity it asks for. Most restrictive wins across owners.
const RIG = 'required';
const RIG_OWNER = 'neonpulse';
const RIG_INTENSITY_SCALE = 1;
const RIG_ZONES = 'default';
// Same lit-floor treatment as kinetic-rig.js: relaxes the rig's spot falloff
// (decay 2.4 -> 1.1) so the beams actually reach the ground and draw pools
// on it, instead of arriving divided by ~170 and leaving the floor black.
// Zones stay 'default' here - NeonPulse wants the lit floor, not the
// off-centre aim constraint KineticRig needs.
const RIG_FLOOR_LIGHT_BOOST = 3;
export { RIG };

// The floor has to be able to catch light at all: 0x08080a is ~0.0008 in
// linear space once sRGB-decoded, dark enough that no spotlight can register
// on it. Widened to 60x60 too, so the pools do not run off a 30-unit plane.
const FLOOR_SIZE = 60;
const FLOOR_COLOR = 0x16161c;

// NeonPulse: the main party mode. The rig + camera + floor here are
// permanent for as long as this visualizer is active - unlike switching
// between top-level visualizers (which tears down and rerolls the whole
// rig), the *particle layer* alone rotates every MIN_LAYER_S-MAX_LAYER_S
// seconds between dust/storm, at random, without ever touching the
// projectors. That's the point: keep the part that's already good (the
// rig) exactly as it is, and only vary "the other aspects" for interest
// over a track's length.
//
// streak (the Tunnel layer) is deliberately excluded from this rotation -
// it stays available standalone via the debug visualizer switcher
// (visualizers/tunnel.js still imports StreakLayer directly), just not
// mixed into the main party-mode cycle.
const LAYER_TYPES = { dust: DustLayer, storm: StormLayer };
const MIN_LAYER_S = 10;
const MAX_LAYER_S = 30;

export class NeonPulseVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, 2.6, 7);
    camera.lookAt(0, 2, 0);

    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES,
      floorLightBoost: RIG_FLOOR_LIGHT_BOOST
    });

    const floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const floorMaterial = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR,
      roughness: 0.78,
      metalness: 0.0
    });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    scene.add(this._floor);

    this._time = 0;
    this._currentLayer = null;
    this._currentLayerName = null;
    this._nextLayerSwitchAt = 0;
    this._switchToLayer('dust');
  }

  _switchToLayer(name) {
    if (this._currentLayer) this._currentLayer.dispose(this._scene);
    const LayerClass = LAYER_TYPES[name];
    this._currentLayer = new LayerClass();
    this._currentLayer.init(this._scene, this._camera);
    this._currentLayerName = name;
    this._nextLayerSwitchAt = this._time + MIN_LAYER_S + Math.random() * (MAX_LAYER_S - MIN_LAYER_S);
  }

  update(deltaTime, profile) {
    stageRig.update(deltaTime, profile, RIG_OWNER);

    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;

    if (!paused && this._time >= this._nextLayerSwitchAt) {
      // Never the same layer twice in a row - pick among the other two.
      const candidates = Object.keys(LAYER_TYPES).filter((n) => n !== this._currentLayerName);
      this._switchToLayer(candidates[Math.floor(Math.random() * candidates.length)]);
    }

    this._currentLayer.update(deltaTime, profile, this._rig);
  }

  onPlayPause() {}

  getLightRig() {
    return this._rig;
  }

  dispose() {
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._currentLayer.dispose(this._scene);
  }
}
